import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Search, PlusCircle, MoreHorizontal, Edit, Trash2, Key, Upload, AlertCircle, CheckCircle2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import Pagination from "@/components/admin/pagination";
import { useAdminAuth } from "@/context/Admin";
import Loader from "@/components/loader/loader";

export default function AdminUsers() {
  const { getAllUsers, createUser, updateUser, hardDeleteUser, resetUserPassword, importUsers } = useAdminAuth();
  
  // Server-side pagination state
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  
  // Add User Dialog
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  // Edit User Dialog
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
  });

  // Confirmation Dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [processing, setProcessing] = useState(false);

  // Import Users Dialog
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importData, setImportData] = useState("");
  const [importResults, setImportResults] = useState(null);
  const [importStep, setImportStep] = useState("input"); // "input" | "preview" | "results"

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch users when page, limit, or search changes
  useEffect(() => {
    fetchUsers();
  }, [currentPage, rowsPerPage, debouncedSearchTerm]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const data = await getAllUsers(currentPage, rowsPerPage, "active", debouncedSearchTerm);
      // Update users and pagination from API response
      setUsers(data.users || []);
      setPagination({
        total: data.total || 0,
        totalPages: data.totalPages || 0,
        hasNextPage: data.hasNextPage || false,
        hasPrevPage: data.hasPrevPage || false,
      });
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setUsers([]);
      setPagination({
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get display name
  const getUserDisplayName = (user) => {
    const firstName = user.first_name || "";
    const lastName = user.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || user.email || "Unknown User";
  };

  // Helper to check if user is deleted
  const isUserDeleted = (user) => {
    return user.is_deleted === true || user.status === "Inactive" || user.status === "deleted";
  };

  // Edit User
  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      phone_number: user.phone_number || "",
    });
    setIsEditUserOpen(true);
  };

  const handleCreateUser = async () => {
    if (!newUser.firstName || !newUser.lastName || !newUser.email || !newUser.phone || !newUser.password) {
      return;
    }

    if (newUser.password.length < 8) {
      alert("Password must be at least 8 characters long.");
      return;
    }

    setProcessing(true);
    try {
      await createUser({
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        email: newUser.email,
        phone: newUser.phone,
        password: newUser.password,
        isVerified: true,
      });
      await fetchUsers(); // Refresh users list
      setIsAddUserOpen(false);
      setNewUser({ firstName: "", lastName: "", email: "", phone: "", password: "", confirmPassword: "" });
    } catch (error) {
      console.error("Error creating user:", error);
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    setProcessing(true);
    try {
      await updateUser(editingUser.id, editForm);
      await fetchUsers(); // Refresh users list
      setIsEditUserOpen(false);
      setEditingUser(null);
    } catch (error) {
      console.error("Error updating user:", error);
    } finally {
      setProcessing(false);
    }
  };

  // Delete User
  const handleDeleteClick = (user) => {
    setSelectedUserId(user.id);
    setSelectedUserName(getUserDisplayName(user));
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUserId) return;

    setProcessing(true);
    try {
      await hardDeleteUser(selectedUserId);
      await fetchUsers(); // Refresh users list
      setDeleteDialogOpen(false);
      setSelectedUserId(null);
      setSelectedUserName("");
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setProcessing(false);
    }
  };

  // Reset Password
  const handleResetPasswordClick = (user) => {
    setSelectedUserId(user.id);
    setSelectedUserName(getUserDisplayName(user));
    setResetPasswordDialogOpen(true);
  };

  const handleConfirmResetPassword = async () => {
    if (!selectedUserId) return;

    setProcessing(true);
    try {
      await resetUserPassword(selectedUserId);
      setResetPasswordDialogOpen(false);
      setSelectedUserId(null);
      setSelectedUserName("");
    } catch (error) {
      console.error("Error resetting password:", error);
    } finally {
      setProcessing(false);
    }
  };

  // Parse import data from textarea
  const parseImportData = (text) => {
    const lines = text.trim().split("\n").filter(line => line.trim());
    const users = [];
    const errors = [];

    lines.forEach((line, index) => {
      // Support both comma and tab separated values
      const parts = line.includes("\t") ? line.split("\t") : line.split(",");
      const trimmedParts = parts.map(p => p.trim());

      if (trimmedParts.length >= 4) {
        const [firstName, lastName, email, phone] = trimmedParts;
        if (firstName && lastName && email && phone) {
          users.push({ firstName, lastName, email, phone });
        } else {
          errors.push(`Line ${index + 1}: Missing required fields`);
        }
      } else if (trimmedParts.length >= 3) {
        // Try to parse as "Full Name, Email, Phone"
        const [fullName, email, phone] = trimmedParts;
        const nameParts = fullName.trim().split(/\s+/);
        if (nameParts.length >= 2 && email && phone) {
          const firstName = nameParts[0];
          const lastName = nameParts.slice(1).join(" ");
          users.push({ firstName, lastName, email, phone });
        } else {
          errors.push(`Line ${index + 1}: Invalid format`);
        }
      } else {
        errors.push(`Line ${index + 1}: Not enough fields (need: FirstName, LastName, Email, Phone)`);
      }
    });

    return { users, errors };
  };

  const handleImportPreview = () => {
    const { users, errors } = parseImportData(importData);
    if (users.length === 0 && errors.length === 0) {
      return;
    }
    setImportResults({ users, errors, successful: [], failed: [] });
    setImportStep("preview");
  };

  const handleImportSubmit = async () => {
    if (!importResults?.users?.length) return;

    setProcessing(true);
    try {
      const response = await importUsers(importResults.users);
      setImportResults(prev => ({
        ...prev,
        successful: response.results.successful,
        failed: response.results.failed,
      }));
      setImportStep("results");
      await fetchUsers(); // Refresh users list
    } catch (error) {
      console.error("Error importing users:", error);
    } finally {
      setProcessing(false);
    }
  };

  const resetImportDialog = () => {
    setIsImportOpen(false);
    setImportData("");
    setImportResults(null);
    setImportStep("input");
  };

  // No client-side filtering needed - server handles it
  // Users are already paginated from the server

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Users</h2>
          <p className="text-sm text-gray-500">
            Manage your customer users.
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            className="h-8 gap-1"
            onClick={() => setIsImportOpen(true)}
          >
            <Upload className="h-3.5 w-3.5" />
            <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">
              Import Users
            </span>
          </Button>
          <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="h-8 gap-1">
                <PlusCircle className="h-3.5 w-3.5" />
                <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">
                  Add New User
                </span>
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
              <DialogDescription>
                Create a new user account. An email will be sent to the user with their login credentials.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="firstName" className="text-right">First Name *</Label>
                <Input
                  id="firstName"
                  value={newUser.firstName}
                  onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                  className="col-span-3"
                  placeholder="Enter first name"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="lastName" className="text-right">Last Name *</Label>
                <Input
                  id="lastName"
                  value={newUser.lastName}
                  onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                  className="col-span-3"
                  placeholder="Enter last name"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="email" className="text-right">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="col-span-3"
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="phone" className="text-right">Phone *</Label>
                <Input
                  id="phone"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  className="col-span-3"
                  placeholder="Enter phone number (e.g., +234...)"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="password" className="text-right">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="col-span-3"
                  placeholder="Enter password (min 8 characters)"
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsAddUserOpen(false);
                setNewUser({ firstName: "", lastName: "", email: "", phone: "", password: "", confirmPassword: "" });
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateUser}
                disabled={processing || !newUser.firstName || !newUser.lastName || !newUser.email || !newUser.phone || !newUser.password || newUser.password.length < 8}
              >
                {processing ? "Creating..." : "Create User"}
              </Button>
            </DialogFooter>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search users by name, email, or phone..."
          className="w-full rounded-lg bg-background pl-8"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto relative">
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {/* Delete Loading Overlay */}
            {processing && (
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <Loader />
                  <p className="text-sm text-gray-600">Deleting user...</p>
                </div>
              </div>
            )}
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Email Verified</TableHead>
                <TableHead className="hidden md:table-cell">Created At</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length > 0 ? (
                users.map((user) => {
                  const isDeleted = isUserDeleted(user);
                  return (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {getUserDisplayName(user)}
                      </TableCell>
                      <TableCell>{user.email || "N/A"}</TableCell>
                      <TableCell>{user.phone_number || "N/A"}</TableCell>
                      <TableCell>
                        <Badge variant={isDeleted ? "outline" : "default"}>
                          {isDeleted ? "Inactive" : "Active"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={user.is_verified ? "default" : "outline"}
                          className={user.is_verified ? "bg-green-500" : ""}
                        >
                          {user.is_verified ? "Verified" : "Not Verified"}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {user.created_at 
                          ? new Date(user.created_at).toLocaleDateString()
                          : "N/A"}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditUser(user)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteClick(user)}
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleResetPasswordClick(user)}>
                              <Key className="mr-2 h-4 w-4" />
                              Reset Password
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-4 text-muted-foreground"
                  >
                    No users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-600">
            Showing {users.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0} to{" "}
            {Math.min(currentPage * rowsPerPage, pagination.total)} of {pagination.total} users
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={pagination.totalPages}
            onPageChange={setCurrentPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(value) => {
              setRowsPerPage(value);
              setCurrentPage(1);
            }}
          />
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={isEditUserOpen} onOpenChange={setIsEditUserOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information. Click save when you're done.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-first-name" className="text-right">First Name</Label>
              <Input
                id="edit-first-name"
                value={editForm.first_name}
                onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-last-name" className="text-right">Last Name</Label>
              <Input
                id="edit-last-name"
                value={editForm.last_name}
                onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-email" className="text-right">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-phone" className="text-right">Phone</Label>
              <Input
                id="edit-phone"
                value={editForm.phone_number}
                onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditUserOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={processing}>
              {processing ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the user "{selectedUserName}" and all their associated data (orders, transactions, tickets, etc.). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={processing}
              className="bg-red-600 hover:bg-red-700"
            >
              {processing ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Confirmation Dialog */}
      <AlertDialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Password?</AlertDialogTitle>
            <AlertDialogDescription>
              This will generate a new password for "{selectedUserName}" and send it to their email address. They will be required to change it on next login.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmResetPassword}
              disabled={processing}
            >
              {processing ? "Resetting..." : "Reset Password"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Users Dialog */}
      <Dialog open={isImportOpen} onOpenChange={(open) => !open && resetImportDialog()}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {importStep === "input" && "Import Existing Customers"}
              {importStep === "preview" && "Preview Import"}
              {importStep === "results" && "Import Results"}
            </DialogTitle>
            <DialogDescription>
              {importStep === "input" && "Paste customer data below. Each line should contain: FirstName, LastName, Email, Phone (comma or tab separated). No password needed - customers will receive an email to set up their password."}
              {importStep === "preview" && "Review the data before importing. Customers will receive an email invitation to set up their password."}
              {importStep === "results" && "Import complete. See the results below."}
            </DialogDescription>
          </DialogHeader>

          {importStep === "input" && (
            <div className="grid gap-4 py-4">
              <Textarea
                placeholder={`John, Doe, john@example.com, +2348012345678\nJane, Smith, jane@example.com, +2348098765432\n\nOr with full name:\nJohn Doe, john@example.com, +2348012345678`}
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                className="min-h-[200px] font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Supported formats: FirstName, LastName, Email, Phone OR Full Name, Email, Phone
              </p>
            </div>
          )}

          {importStep === "preview" && importResults && (
            <div className="grid gap-4 py-4">
              {importResults.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                    <AlertCircle className="h-4 w-4" />
                    Parse Errors ({importResults.errors.length})
                  </div>
                  <ul className="text-sm text-red-600 list-disc pl-5">
                    {importResults.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                    {importResults.errors.length > 5 && (
                      <li>...and {importResults.errors.length - 5} more errors</li>
                    )}
                  </ul>
                </div>
              )}

              {importResults.users.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Ready to Import ({importResults.users.length} users)
                  </div>
                  <div className="max-h-[200px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-green-700">
                          <th className="pb-1">Name</th>
                          <th className="pb-1">Email</th>
                          <th className="pb-1">Phone</th>
                        </tr>
                      </thead>
                      <tbody className="text-green-600">
                        {importResults.users.slice(0, 10).map((user, i) => (
                          <tr key={i}>
                            <td className="py-0.5">{user.firstName} {user.lastName}</td>
                            <td className="py-0.5">{user.email}</td>
                            <td className="py-0.5">{user.phone}</td>
                          </tr>
                        ))}
                        {importResults.users.length > 10 && (
                          <tr>
                            <td colSpan={3} className="pt-2 text-green-500">
                              ...and {importResults.users.length - 10} more users
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {importStep === "results" && importResults && (
            <div className="grid gap-4 py-4">
              {importResults.successful.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Successfully Imported ({importResults.successful.length})
                  </div>
                  <p className="text-sm text-green-600">
                    Email invitations have been sent to all successfully imported users.
                  </p>
                </div>
              )}

              {importResults.failed.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                    <AlertCircle className="h-4 w-4" />
                    Failed ({importResults.failed.length})
                  </div>
                  <ul className="text-sm text-red-600 list-disc pl-5 max-h-[150px] overflow-y-auto">
                    {importResults.failed.map((item, i) => (
                      <li key={i}>{item.email}: {item.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {importStep === "input" && (
              <>
                <Button variant="outline" onClick={resetImportDialog}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleImportPreview}
                  disabled={!importData.trim()}
                >
                  Preview
                </Button>
              </>
            )}

            {importStep === "preview" && (
              <>
                <Button variant="outline" onClick={() => setImportStep("input")}>
                  Back
                </Button>
                <Button 
                  onClick={handleImportSubmit}
                  disabled={processing || !importResults?.users?.length}
                >
                  {processing ? "Importing..." : `Import ${importResults?.users?.length || 0} Users`}
                </Button>
              </>
            )}

            {importStep === "results" && (
              <Button onClick={resetImportDialog}>
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
